import type { TaskCreateFields, TaskPriority } from '@tradeoutlet/shared';
import { Field, Input, Select } from '@/components/ui';
import { useCanAnywhere } from '@/lib/permissions';
import { useBuildings } from '@/features/buildings/common';
import { AssigneePicker } from '@/features/people/AudiencePicker';
import { AreaSelect, BudgetInput, EstimateInput, MoreSection } from './taskExtras';

/** Who a task is for, and when (backlog #47). Shared by "New task" and "Create task from message". */
/** t168u: derived from the shared TaskCreateFields, so every field here is one the task APIs accept. */
export type TaskFor = Required<Pick<TaskCreateFields, 'assignee_ids' | 'team_ids' | 'location_id' | 'due_date' | 'priority'>>
  /** t118: area in the building, estimate and budget (the "More" section). */
  & Pick<TaskCreateFields, 'area_id' | 'estimate_minutes' | 'budget_pence'>;
export const taskForDefaults = (me: string | null, assignMe = true, locationId: string | null = null): TaskFor => ({ assignee_ids: me && assignMe ? [me] : [], team_ids: [], location_id: locationId, due_date: null, priority: 'none', area_id: null, estimate_minutes: null, budget_pence: null });

export const PRIORITY_LABELS: Record<TaskPriority, string> = { none: 'No priority', low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent' };

/**
 * With no project, giving the task to other people, teams or buildings needs "Assign tasks to others"; without it
 * the task is just for you. The server checks the exact scope.
 */
export function TaskForFields({ value, onChange, standalone, me, extra, extraCount }: { value: TaskFor; onChange: (v: TaskFor) => void; standalone: boolean; me: string | null; /** More-section fields that depend on the project (Milestone). */ extra?: React.ReactNode; /** Filled fields inside `extra`, for the More count. */ extraCount?: number }) {
  const canAssign = useCanAnywhere('tasks.task.assign');
  const buildings = useBuildings().data?.buildings ?? [];
  const open = !standalone || canAssign;
  const set = (p: Partial<TaskFor>) => onChange({ ...value, ...p });
  return (
    <div className="space-y-4">
      {open ? <>
        <Field label="Assignees"><AssigneePicker assigneeIds={value.assignee_ids} teamIds={value.team_ids} onChange={v => set(v)} /></Field>
        <Field label="Building">
          <Select value={value.location_id ?? ''} onChange={e => set({ location_id: e.target.value || null, area_id: null })}>
            <option value="">None</option>
            {buildings.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Select>
        </Field>
        {value.location_id && <Field label="Area"><AreaSelect locationId={value.location_id} value={value.area_id} onChange={id => set({ area_id: id })} /></Field>}
      </> : (
        <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600 dark:bg-slate-800/60 dark:text-slate-300" data-testid="task-for-me">
          This task is for you{value.assignee_ids.includes(me ?? '') ? '' : ' (not assigned yet)'}. To give tasks to other people, ask a manager for <strong>Assign tasks to others</strong>, or put it in a project.
        </p>
      )}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Due date"><Input type="date" value={value.due_date ?? ''} onChange={e => set({ due_date: e.target.value || null })} /></Field>
        <Field label="Priority">
          <Select value={value.priority} onChange={e => set({ priority: e.target.value as TaskPriority })}>
            {(Object.keys(PRIORITY_LABELS) as TaskPriority[]).map(p => <option key={p} value={p}>{PRIORITY_LABELS[p]}</option>)}
          </Select>
        </Field>
      </div>
      <MoreSection count={[value.estimate_minutes, value.budget_pence].filter(v => v != null).length + (extraCount ?? 0)}>
        {extra}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Estimate"><EstimateInput value={value.estimate_minutes} onChange={m => set({ estimate_minutes: m })} /></Field>
          <Field label="Budget"><BudgetInput value={value.budget_pence} onChange={p => set({ budget_pence: p })} /></Field>
        </div>
      </MoreSection>
    </div>
  );
}
